import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../utils/api';
import { BarChart2, Building2, CalendarDays, CalendarRange, AlertTriangle } from 'lucide-react';
import { acqDenominator, invalidSeatCount } from '../utils/seat-rate.mjs';

const card = { background: 'var(--field)', border: '1px solid var(--rule-strong)', boxShadow: 'none', borderRadius: 16, padding: '16px 18px', marginBottom: 12 };
const selStyle = { padding: '10px 12px', background: 'var(--paper-sunk)', border: '1px solid var(--rule-strong)', borderRadius: 10, color: 'var(--ink)', fontSize: 14, minHeight: 44 };

// 1日平均で見せる6指標
const METRICS = [
  { key: 'catch',     label: 'キャッチ',   color: 'var(--mark)' },
  { key: 'sticker',   label: 'ティッシュ回収', color: 'var(--mark)' },
  { key: 'talk',      label: 'トーク開始', color: 'var(--mark)' },
  { key: 'full_talk', label: 'フルトーク', color: 'var(--mark)' },
  { key: 'seat',      label: '着座',       color: 'var(--mark)' },
  { key: 'acq',       label: '獲得',       color: 'var(--gain)' },
];

// 5段階の転換率（声かけ→ティッシュ→トーク→フルトーク→着座→獲得）
const RATE_STAGES = [
  { key: 'catchSticker', label: '声かけ→ティッシュ' },
  { key: 'stickerTalk',  label: 'ティッシュ→トーク' },
  { key: 'talkFulltalk', label: 'トーク→フルトーク' },
  { key: 'fulltalkSeat', label: 'フルトーク→着座' },
  { key: 'seatAcq',      label: '着座→獲得' },
];

const GROUPS = [
  { key: 'booth', label: 'ブース別', Icon: Building2 },
  { key: 'week',  label: '週別',     Icon: CalendarDays },
  { key: 'month', label: '月別',     Icon: CalendarRange },
];

// 基準割れ判定（findings.rule_key）→ 表示中の転換率セルへの対応（赤表示用）。
// talk_to_seat はフルトーク新設前の旧ルールで、トーク→フルトーク→着座の2段にまたがるため両方を赤くする。
const RULE_TO_RATE = {
  catch_to_sticker:  ['catchSticker'],
  sticker_to_talk:   ['stickerTalk'],
  talk_to_fulltalk:  ['talkFulltalk'],
  fulltalk_to_seat:  ['fulltalkSeat'],
  talk_to_seat:      ['talkFulltalk', 'fulltalkSeat'],
  seat_to_acq:       ['seatAcq'],
};

const todayJst = () => new Date(Date.now() + 9 * 3600000).toISOString().slice(0, 10);
const fmtRate = (v) => (v == null ? '-' : `${v.toFixed(1)}%`);

// 期間プリセット。`today`（'YYYY-MM-DD' JST）を引数に取り、日付境界をまたいでも
// state の today が更新されれば当日まで含む範囲を再計算できるようにする。
function rangeFor(preset, today) {
  if (preset === 'all') return { date_from: '', date_to: '' };
  const [y, m1] = today.split('-').map(Number);
  const m = m1 - 1; // 0-indexed month
  const pad = (n) => String(n).padStart(2, '0');
  if (preset === 'this_month') {
    return { date_from: `${y}-${pad(m + 1)}-01`, date_to: today };
  }
  if (preset === 'last_month') {
    const lm = new Date(Date.UTC(y, m - 1, 1));
    const last = new Date(Date.UTC(y, m, 0));
    return { date_from: lm.toISOString().slice(0, 10), date_to: last.toISOString().slice(0, 10) };
  }
  // 直近3ヶ月
  const from = new Date(Date.UTC(y, m - 2, 1));
  return { date_from: from.toISOString().slice(0, 10), date_to: today };
}

const PRESETS = [
  { key: 'this_month', label: '今月' },
  { key: 'last_month', label: '先月' },
  { key: '3m',         label: '直近3ヶ月' },
  { key: 'all',        label: '全期間' },
];

export default function AverageCheck({ user }) {
  const [groupBy, setGroupBy] = useState('booth');
  const [preset, setPreset] = useState('this_month');
  const [userId, setUserId] = useState('');
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [today, setToday] = useState(todayJst());

  useEffect(() => { api.getUsers().then(setUsers).catch(() => setUsers([])); }, []);

  // 日付またぎ対策：PWAを開いたまま/復帰で日付が変わったら today を更新して当日を範囲に含める。
  // 同一プリセットの再タップでは useMemo が再計算されず「今日」が前日固定のままになる不具合の対策。
  // （setToday は同値なら同一参照を返すため、日付が変わらない限り再フェッチは発生しない）
  useEffect(() => {
    const check = () => setToday((prev) => { const t = todayJst(); return t === prev ? prev : t; });
    const onVis = () => { if (document.visibilityState === 'visible') check(); };
    const id = setInterval(check, 30000);
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', check);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVis); window.removeEventListener('focus', check); };
  }, []);

  const range = useMemo(() => rangeFor(preset, today), [preset, today]);

  useEffect(() => {
    setLoading(true);
    const params = { group_by: groupBy };
    if (userId) params.user_id = userId;
    if (range.date_from) params.date_from = range.date_from;
    if (range.date_to) params.date_to = range.date_to;
    api.getAnalyticsAverages(params)
      .then(d => setGroups(Array.isArray(d.groups) ? d.groups : []))
      .catch(() => setGroups([]))
      .finally(() => setLoading(false));
  }, [groupBy, userId, range]);

  return (
    <div style={{ maxWidth: 1120, margin: '0 auto', padding: 'var(--s6) var(--s4) 120px' }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)' }}>1日あたりの平均と転換率</div>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: 'var(--ink)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <BarChart2 size={20} /> 平均確認
        </h1>
      </div>

      {/* グループ切替タブ */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {GROUPS.map(({ key, label, Icon }) => {
          const active = groupBy === key;
          return (
            <button key={key} onClick={() => setGroupBy(key)} style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              padding: '12px 8px', borderRadius: 12, minHeight: 48,
              border: `1.5px solid ${active ? '#33406E' : 'var(--rule-strong)'}`,
              background: active ? 'var(--mark-soft)' : 'transparent',
              color: active ? 'var(--mark)' : 'var(--ink-soft)', fontWeight: 700, fontSize: 14, cursor: 'pointer',
            }}>
              <Icon size={16} /> {label}
            </button>
          );
        })}
      </div>

      {/* フィルタ：担当者 + 期間 */}
      <div style={{ ...card, padding: '12px 14px' }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
          <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-soft)', fontWeight: 600 }}>担当者</span>
          <select value={userId} onChange={e => setUserId(e.target.value)} style={{ ...selStyle, flex: 1, minWidth: 140 }}>
            <option value="">全員</option>
            {users.filter(u => u.is_active !== 0).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {PRESETS.map(p => {
            const active = preset === p.key;
            return (
              <button key={p.key} onClick={() => setPreset(p.key)} style={{
                padding: '8px 14px', borderRadius: 10, minHeight: 40,
                border: `1.5px solid ${active ? '#33406E' : 'var(--rule-strong)'}`,
                background: active ? 'var(--mark-soft)' : 'transparent',
                color: active ? 'var(--mark)' : 'var(--ink-soft)', fontWeight: 700, fontSize: 'var(--t-note)', cursor: 'pointer',
              }}>{p.label}</button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', color: 'var(--ink-mute)', padding: 40 }}>読み込み中...</div>
      ) : groups.length === 0 ? (
        <div style={{ textAlign: 'center', color: 'var(--ink-soft)', padding: 60 }}>該当データがありません</div>
      ) : (
        groups.map(g => {
          const findings = g.findings || [];
          const belowSet = new Set(findings.flatMap(f => RULE_TO_RATE[f.rule_key] || []));
          return (
          <div key={g.key} style={card}>
            {/* グループ名 + 稼働日数 */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--ink)' }}>{g.label}</div>
              <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)' }}>稼働 <strong style={{ color: 'var(--ink)', fontSize: 14 }}>{g.days}</strong> 日</div>
            </div>

            {/* 1日平均件数。スマホ(375px)で6列固定だと「ティッシュ回収」等が入りきらず
                右端の「獲得」が見切れていたため、折り返す可動グリッドにしている。 */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(88px, 1fr))', gap: 6, marginBottom: 12 }}>
              {METRICS.map(m => (
                <div key={m.key} style={{ background: 'var(--paper-sunk)', borderRadius: 10, padding: '8px 4px', textAlign: 'center', border: '1px solid var(--rule-strong)' }}>
                  <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)', marginBottom: 3, whiteSpace: 'nowrap' }}>{m.label}</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: m.color }}>{g.avg?.[m.key] ?? 0}</div>
                  <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-soft)', marginTop: 1 }}>計{g.totals?.[m.key] ?? 0}</div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-soft)', marginBottom: 12 }}>数値は1日あたりの平均（小さい数字は期間合計）</div>

            {/* 転換率（5段階・%）。基準割れは赤表示 */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(96px, 1fr))', gap: 6 }}>
              {RATE_STAGES.map(s => {
                const below = belowSet.has(s.key);
                return (
                  <div key={s.key} style={{ background: below ? 'var(--loss-bg)' : 'var(--mark-soft)', borderRadius: 8, padding: '8px 4px', textAlign: 'center', border: `1px solid ${below ? '#6B3A36' : '#33406E'}` }}>
                    <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-soft)', whiteSpace: 'nowrap', marginBottom: 2 }}>{s.label}</div>
                    <div style={{ fontSize: 15, fontWeight: 800, color: below ? 'var(--loss)' : 'var(--mark)' }}>{fmtRate(g.rates?.[s.key])}</div>
                    {/* 着座→獲得の分母は有効着座。「有効じゃない」を除いたときだけ件数を添える（ADR 0028） */}
                    {s.key === 'seatAcq' && invalidSeatCount(g.totals) > 0 && (
                      <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)', marginTop: 2 }} title={`「有効じゃない」${invalidSeatCount(g.totals)}件を分母から除いています`}>有効着座 {acqDenominator(g.totals)}</div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* 基準を下回った項目の警告（基準は「基準設定」で編集） */}
            {findings.length > 0 && (
              <div style={{ marginTop: 12, padding: '10px 12px', background: 'var(--hold-bg)', border: '1px solid #5C4718', borderRadius: 10 }}>
                <div style={{ fontSize: 'var(--t-note)', fontWeight: 800, color: 'var(--hold)', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <AlertTriangle size={13} /> 基準を下回った項目（{findings.length}）
                </div>
                {findings.map(f => (
                  <div key={f.rule_key} style={{ marginBottom: 6 }}>
                    <div style={{ fontSize: 'var(--t-note)', color: 'var(--hold)', fontWeight: 700 }}>
                      {f.message} <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-soft)', fontWeight: 600 }}>（{f.value}% ／ 基準 {f.threshold}%）</span>
                    </div>
                    {f.theme && <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-soft)' }}>→ {f.theme}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
          );
        })
      )}
    </div>
  );
}
